"use client";

import { useAuth } from "@workos-inc/authkit-nextjs/components";
import { useConvexAuth, useQuery } from "convex/react";
import { usePathname } from "next/navigation";
import { api } from "@convex/_generated/api";
import { useDelayElapsed } from "@/hooks/use-delay-elapsed";
import { useHasMounted } from "@/hooks/use-has-mounted";
import { buildAuthRecoveryHref } from "@/lib/auth-recovery";
import type { ProductDetail } from "./types";
import { LockedListingActionCard } from "./listing-action-locked-card";
import { ListingActionSkeleton } from "./listing-action-skeleton";
import { ResolvingAction } from "./listing-action-resolution";
import { ListingOwnerActionCard } from "./listing-owner-action-card";
import { SignedOutListingAction, type GateAction } from "./listing-signed-out-action";
import { useListingQuery, type ListingQuery } from "./listing-query";


interface ListingActionGateProps {
  data: ProductDetail;
  children: React.ReactNode;
  preview?: GateAction;
}

const AUTH_RECOVERY_DELAY_MS = 2_000;

export function ListingActionGate({
  data,
  children,
  preview,
}: ListingActionGateProps) {
  const pathname = usePathname();
  const searchParams = useListingQuery();
  const auth = useListingActionAuth(data._id);

  if (auth.needsRecovery) {
    return (
      <ListingAuthRecovery pathname={pathname} searchParams={searchParams} />
    );
  }

  if (auth.isAuthenticated && !auth.isLoading) {
    if (auth.listingRelationship?.ownsListing) {
      return <ListingOwnerActionCard productId={data._id} />;
    }
    return (
      <ResolvingAction isResolving={auth.listingRelationship === undefined}>
        {children}
      </ResolvingAction>
    );
  }

  return (
    <ResolvingAction isResolving={auth.isLoading}>
      <SignedOutListingAction data={data} preview={preview}>
        {children}
      </SignedOutListingAction>
    </ResolvingAction>
  );
}

function useListingActionAuth(productId: ProductDetail["_id"]) {
  const hasMounted = useHasMounted();
  const { user, loading } = useAuth();
  const convexAuth = useConvexAuth();
  const isAuthenticated = Boolean(user && convexAuth.isAuthenticated);
  const listingRelationship = useQuery(
    api.products.ownershipQueries.getMyListingRelationship,
    isAuthenticated ? { productId } : "skip",
  );

  return {
    isAuthenticated,
    isLoading: !hasMounted || loading || convexAuth.isLoading,
    listingRelationship,
    needsRecovery: Boolean(
      hasMounted && user && !loading && !convexAuth.isLoading && !convexAuth.isAuthenticated,
    ),
  };
}

function ListingAuthRecovery({
  pathname,
  searchParams,
}: {
  pathname: string;
  searchParams: ListingQuery;
}) {
  const didGracePeriodElapse = useDelayElapsed(AUTH_RECOVERY_DELAY_MS);
  if (!didGracePeriodElapse) return <ListingActionSkeleton />;

  return (
    <LockedListingActionCard
      pathname={pathname}
      searchParams={searchParams}
      description="Your sign-in could not connect. Reconnect to continue."
      buildPrimaryHref={buildAuthRecoveryHref}
      primaryLabel="Reconnect"
      secondaryHref="/sign-out"
      secondaryLabel="Sign out"
    />
  );
}
